// src/store/workflow/workflowThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	getTaskVariables,
	deletePendingTask,
	deleteWorkflowProcess,
	getProcessDocuments,
	getProcessDocumentDownload,
	getFormByUserTask,
	setAssigneeForTask,
	putProcessInstanceVariable,
	postRegeneratePDF,
	unclaimPendingTask,
	claimTask,
	cloneWorkflow,
	startWorkflowProcess,
	startWorkflowProcessByKey,
	getWorkflowsList,
	GetWorkflowHistoryFromSearchIndex,
	executePendingTask,
	getTaskHistoryData,
} from "api/workflowsApi";
import { logger } from "helpers/logger";
import { fetchWorkflowHistoriesSuccess } from "../slices/workflows/workflowHistoriesSlice";

// Thunk for fetching workflow histories
export const fetchWorkflowHistoriesThunk = createAsyncThunk(
	"workflow/fetchWorkflowHistories",
	async (params, { dispatch, getState, rejectWithValue, signal }) => {
		try {
			const response = await GetWorkflowHistoryFromSearchIndex(
				params.assignee,
				{
					StartDateTime: params.startDate,
					EndDateTime: params.endDate,
					SearchParam: params.searchParam,
					SortField: params.sortField,
					SortDirection: params.sortDirection,
					RecordsByPage: params.recordsByPage,
					PageNumber: params.pageNumber,
					Lang: getState().translations.currentLanguage,
				},
				signal
			);

			dispatch(fetchWorkflowHistoriesSuccess(response));

			return response;
		} catch (err) {
			return rejectWithValue(err.response ?? err.message);
		}
	}
);
// Thunk for getting task variables
export const fetchTaskVariables = createAsyncThunk("workflow/fetchTaskVariables", async ({ processInstanceId }, { rejectWithValue, signal }) => {
	try {
		const response = await getTaskVariables(processInstanceId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch task variables:", error);
		return rejectWithValue(error.message || "Failed to fetch task variables");
	}
});

export const deletePendingTaskThunk = createAsyncThunk("workflow/deletePendingTask", async ({ rootProcessInstanceId }, { rejectWithValue, signal }) => {
	try {
		const response = await deletePendingTask({ task_id: rootProcessInstanceId }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to delete pending task:", error);
		return rejectWithValue(error.message || "Failed to delete task");
	}
});

// Documents generated so far for a process (company from the store; checked by the backend).
export const fetchProcessDocumentsThunk = createAsyncThunk("workflow/fetchProcessDocuments", async ({ processInstanceId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await getProcessDocuments({ processInstanceId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch process documents:", error);
		return rejectWithValue(error.message || "Failed to fetch documents");
	}
});

// A short-lived download link for one document (company from the store; checked by the backend).
export const fetchDocumentDownloadThunk = createAsyncThunk("workflow/fetchDocumentDownload", async ({ documentId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await getProcessDocumentDownload({ documentId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get document download link:", error);
		return rejectWithValue(error.message || "Failed to download the document");
	}
});

// Pending tasks page: delete through api/WorkflowTasks/Delete (checked by the backend).
// WorkflowHistoryPage still uses deletePendingTaskThunk until that page is migrated.
export const deleteWorkflowProcessThunk = createAsyncThunk("workflow/deleteWorkflowProcess", async ({ processInstanceId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await deleteWorkflowProcess({ processInstanceId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to delete process:", error);
		return rejectWithValue(error.message || "Failed to delete process");
	}
});

export const fetchFormByUserTaskThunk = createAsyncThunk("workflow/fetchFormByUserTask", async (params, { rejectWithValue, signal }) => {
	try {
		const { processInstanceId } = params;
		const response = await getFormByUserTask(processInstanceId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch form by user task:", error);
		return rejectWithValue(error.message || "Failed to fetch form by user task");
	}
});

export const putAssigneeForTaskThunk = createAsyncThunk("workflow/putAssigneeForTaskThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const assigneeData = params;
		const response = await setAssigneeForTask(assigneeData, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to set assignee:", error);
		return rejectWithValue(error.message || "Failed to set assignee");
	}
});

export const putProcessInstanceVariableThunk = createAsyncThunk("workflow/putProcessInstanceVariableThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { processInstanceId, variableName, jsonData } = params;
		const response = await putProcessInstanceVariable(processInstanceId, variableName, jsonData, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to put process instance variable:", error);
		return rejectWithValue(error.message || "Failed to put process instance variable");
	}
});

export const postRegeneratePDFThunk = createAsyncThunk("workflow/postRegeneratePDFThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { applicationID, templateId, tableId } = params;
		const response = await postRegeneratePDF(applicationID, templateId, tableId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to regenerate PDF:", error);
		return rejectWithValue(error.message || "Failed to regenerate PDF");
	}
});

export const unclaimPendingTaskThunk = createAsyncThunk("workflow/unclaimPendingTaskThunk", async ({ taskId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		const response = await unclaimPendingTask({ taskId, companyId }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to unclaim pending task:", error);
		return rejectWithValue(error.message || "Failed to Unclaim Task");
	}
});

export const claimTaskThunk = createAsyncThunk("workflow/claimTaskThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { taskId, userId } = params;
		const response = await claimTask(taskId, userId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to claim task:", error);
		return rejectWithValue(error.message || "Failed to Claim Task");
	}
});

export const cloneWorkflowThunk = createAsyncThunk("workflow/cloneWorkflowThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { applicationId, assignee, processInstanceId } = params;
		const response = await cloneWorkflow(applicationId, assignee, processInstanceId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to clone workflow:", error);
		return rejectWithValue(error.message || "Failed to Clone Workflow");
	}
});

export const startWorkflowProcessThunk = createAsyncThunk("workflow/startWorkflowProcessThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { definitionKey, assignee } = params;
		const response = await startWorkflowProcess(definitionKey, assignee, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to start workflow process:", error);
		return rejectWithValue(error.message || "Failed to Start Workflow Process");
	}
});

// Starts the latest deployed version of a process definition KEY (e.g. "tbl_registration"),
// unlike startWorkflowProcessThunk which takes a versioned definition id.
export const startWorkflowProcessByKeyThunk = createAsyncThunk("workflow/startWorkflowProcessByKeyThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { definitionKey, assignee } = params;
		const response = await startWorkflowProcessByKey(definitionKey, assignee, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to start workflow process by key:", error);
		return rejectWithValue(error.message || "Failed to Start Workflow Process");
	}
});

export const getWorkflowsListThunk = createAsyncThunk("workflow/getWorkflowsListThunk", async (params, { rejectWithValue }) => {
	try {
		const { userId, companyId } = params;
		const response = await getWorkflowsList(userId, companyId);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get workflows list:", error);
		return rejectWithValue(error.message || "Failed to Get Workflow List.");
	}
});

export const executePendingTaskThunk = createAsyncThunk("workflow/executePendingTaskThunk", async ({ processInstanceId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		const response = await executePendingTask({ processInstanceId, companyId }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to execute pending task:", error);
		return rejectWithValue(error.message || "Failed to execute pending task");
	}
});

export const getTaskHistoryDataThunk = createAsyncThunk("workflow/getTaskHistoryDataThunk", async ({ processInstanceId, roleIds }, { rejectWithValue, signal }) => {
	try {
		const response = await getTaskHistoryData({ processInstanceId, roleIds }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get task history data:", error);
		return rejectWithValue(error.message || "Failed to get task history data");
	}
});


